import type { OverlapSize } from "@v1/components/designLibrary/ColorPaletteDisplay";
import type { SchemaReploShopifyProduct } from "schemas/generated/product";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { useBrandDetailsTabs } from "@v1/components/designLibrary/BrandDetailsContext";
import ShopStylesColorDisplay from "@v1/components/designLibrary/ShopStylesColorDisplay";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { DeprecatedTextarea } from "@v1/components/designSystem/textarea/Textarea";
import { BuildAssistantSelectProduct } from "@v1/components/products/BuildAssistantSelectProduct";
import { BrandContextPreview } from "@v1/components/saved-styles/BrandContextPreview";
import { setLeftBarActiveTab } from "@v1/reducers/ui-reducer";
import { useEditorDispatch } from "@v1/store/store";
import { Pencil, Send } from "lucide-react";

import ShopStylesFontSample from "../ShopStylesFontSample";
import useBuildAssistantPromptKeyDown from "./useBuildAssistantPromptKeyDown";

interface ExpandedBuildAssistantPromptProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  placeholderText: string;
  onSubmit: () => void;
  selectedProduct: SchemaReploShopifyProduct | null;
  setSelectedProduct: (product: SchemaReploShopifyProduct | null) => void;
  colorOverlapSize?: OverlapSize;
  showFontSample?: boolean;
  showBrandContextPreview?: boolean;
  isGenerating: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  showSendButton?: boolean;
}

export const ExpandedBuildAssistantPrompt: React.FC<
  ExpandedBuildAssistantPromptProps
> = ({
  prompt,
  setPrompt,
  placeholderText,
  selectedProduct,
  setSelectedProduct,
  onSubmit,
  colorOverlapSize = "small",
  showFontSample = true,
  showBrandContextPreview = true,
  isGenerating,
  inputRef,
  showSendButton = true,
}) => {
  const dispatch = useEditorDispatch();
  const { setSelectedTab } = useBrandDetailsTabs();

  const handleKeyDown = useBuildAssistantPromptKeyDown(
    prompt,
    isGenerating,
    onSubmit,
  );

  return (
    <div className="flex flex-col gap-3 p-3">
      <DeprecatedTextarea
        value={prompt}
        onChange={(newPrompt) => setPrompt(newPrompt)}
        placeholder={placeholderText}
        onKeyDown={handleKeyDown}
        isDisabled={isGenerating}
        layoutClassName="h-24"
        size="base"
        ref={inputRef}
        UNSAFE_className="bg-white border-0 no-scrollbar focus:ring-0"
      />

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            className="group relative flex w-fit items-center gap-3 rounded-md"
            onClick={() => {
              dispatch(setLeftBarActiveTab("shopStyles"));
              setSelectedTab("styles");
            }}
          >
            {showFontSample && <ShopStylesFontSample />}
            <ShopStylesColorDisplay overlapSize={colorOverlapSize} />
            <div
              className={twMerge(
                "bg-surface absolute inset-0 z-50 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100",
                showFontSample ? "rounded-md" : "rounded-sm",
              )}
            >
              <Pencil
                className={twMerge(
                  "text-muted h-5 w-5",
                  !showFontSample && "h-4 w-4",
                )}
              />
            </div>
          </button>

          {showBrandContextPreview && <BrandContextPreview />}

          <BuildAssistantSelectProduct
            onProductChange={setSelectedProduct}
            selectedProduct={selectedProduct}
          />
        </div>

        {showSendButton && (
          <DeprecatedIconButton
            size="sm"
            variant="primary"
            onClick={onSubmit}
            aria-label="Send"
            icon={<Send size={12} />}
            disabled={prompt === "" || isGenerating}
          />
        )}
      </div>
    </div>
  );
};
